import React, { useState, useEffect } from 'react';
import { settingsService } from '../../services/settingsService';
import './SiteSettings.css';

const SiteSettings = () => {
  const [settings, setSettings] = useState({
    profileImageUrl: '',
    resumePdfUrl: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await settingsService.getSettings();
      setSettings(data);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please select an image file' });
      return;
    }

    try {
      setUploadingImage(true);
      setMessage({ type: '', text: '' });

      // Delete old image if exists
      if (settings.profileImageUrl) {
        await settingsService.deleteFileByUrl(settings.profileImageUrl);
      }

      // Upload new image
      const downloadURL = await settingsService.uploadProfileImage(file);

      // Update settings
      const updatedSettings = { ...settings, profileImageUrl: downloadURL };
      await settingsService.updateSettings(updatedSettings);
      setSettings(updatedSettings);

      setMessage({ type: 'success', text: 'Profile image updated successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to upload image: ' + error.message });
    } finally {
      setUploadingImage(false);
    }
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'application/pdf') {
      setMessage({ type: 'error', text: 'Please select a PDF file' });
      return;
    }

    try {
      setUploadingPdf(true);
      setMessage({ type: '', text: '' });

      // Delete old PDF if exists
      if (settings.resumePdfUrl) {
        await settingsService.deleteFileByUrl(settings.resumePdfUrl);
      }

      // Upload new PDF
      const downloadURL = await settingsService.uploadResumePdf(file);

      // Update settings
      const updatedSettings = { ...settings, resumePdfUrl: downloadURL };
      await settingsService.updateSettings(updatedSettings);
      setSettings(updatedSettings);

      setMessage({ type: 'success', text: 'Resume PDF updated successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to upload PDF: ' + error.message });
    } finally {
      setUploadingPdf(false);
    }
  };

  const handleUrlChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveUrls = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });
      await settingsService.updateSettings(settings);
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save settings: ' + error.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="site-settings">
        <div className="settings-content">
          <div className="loading">Loading settings...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="site-settings">
      <div className="settings-content">
        {message.text && (
          <div className={`settings-message ${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="settings-grid">
          <div className="settings-section">
            <div className="settings-section-header">
              <h3>Profile Image</h3>
              <p className="settings-description">
                Used on the Home hero, About page, and Resume page.
              </p>
            </div>

            <div className="settings-preview">
              {settings.profileImageUrl ? (
                <img
                  src={settings.profileImageUrl}
                  alt="Profile preview"
                  className="preview-image"
                />
              ) : (
                <div className="preview-placeholder">No image set</div>
              )}
            </div>

            <div className="settings-upload">
              <label className="upload-btn">
                {uploadingImage ? 'Uploading...' : 'Upload New Image'}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploadingImage}
                  hidden
                />
              </label>
            </div>

            <div className="settings-url-input">
              <label>Direct URL</label>
              <input
                type="text"
                value={settings.profileImageUrl}
                onChange={(e) => handleUrlChange('profileImageUrl', e.target.value)}
                placeholder="https://..."
              />
            </div>
          </div>

          <div className="settings-section">
            <div className="settings-section-header">
              <h3>Resume PDF</h3>
              <p className="settings-description">
                The PDF file available for download on the Resume page.
              </p>
            </div>

            <div className="settings-preview">
              {settings.resumePdfUrl ? (
                <div className="pdf-preview">
                  <span className="pdf-icon">PDF</span>
                  <a
                    href={settings.resumePdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="pdf-link"
                  >
                    View Current PDF
                  </a>
                </div>
              ) : (
                <div className="preview-placeholder">No PDF set</div>
              )}
            </div>

            <div className="settings-upload">
              <label className="upload-btn">
                {uploadingPdf ? 'Uploading...' : 'Upload New PDF'}
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={handlePdfUpload}
                  disabled={uploadingPdf}
                  hidden
                />
              </label>
            </div>

            <div className="settings-url-input">
              <label>Direct URL</label>
              <input
                type="text"
                value={settings.resumePdfUrl}
                onChange={(e) => handleUrlChange('resumePdfUrl', e.target.value)}
                placeholder="https://..."
              />
            </div>
          </div>
        </div>

        <div className="settings-actions">
          <button
            className="save-btn"
            onClick={handleSaveUrls}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save URLs'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SiteSettings;
